import { css, Interpolation, Theme } from '@emotion/react'
import { scaleBorderRadius } from 'theme/utils'

export const container = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`

export const tableTitle = css`
  font-weight: 600;
`

export const sectionForm = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  padding: ${theme.spacing(2)};
  border-radius: ${scaleBorderRadius(theme.shape.borderRadius, 5)};
  background-color: ${theme.backgrounds.backgroundFive};
`

export const sectionFormTitle = css`
  font-weight: 600;
`

export const sectionFields = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`

export const sectionActions = (theme: Theme) => css`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing(1.25)};
`

export const table: Interpolation<Theme> = css`
  .table-header .table-cell {
    font-weight: 700;
  }

  .table-row {
    padding: 5px 12px;
  }
`

export const indexCell = css`
  display: block;
  text-align: center;
`

export const reviewerCell = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1.5)};
  min-width: 0;
`

export const previewImage = (theme: Theme) => css`
  width: 50px;
  aspect-ratio: 9 / 16;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: ${scaleBorderRadius(theme.shape.borderRadius, 2)};
`

export const previewEmpty = (theme: Theme) => css`
  width: 50px;
  aspect-ratio: 9 / 16;
  flex-shrink: 0;
  border-radius: ${scaleBorderRadius(theme.shape.borderRadius, 2)};
  background-color: ${theme.backgrounds.backgroundFive};
`

export const avatar = css`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  object-fit: cover;
  border-radius: 50%;
`

export const avatarFallback = (theme: Theme) => css`
  width: 32px;
  height: 32px;
  flex-shrink: 0;
  border-radius: 50%;
  background: ${theme.backgrounds.backgroundTwo};
`

export const reviewerText = css`
  min-width: 0;
`

export const subtleText = (theme: Theme) => css`
  display: block;
  overflow: hidden;
  color: ${theme.typographyColors.typographyThree};
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const statusText = css`
  font-weight: 600;
  text-transform: capitalize;
`

export const statusProcessing = (theme: Theme) => css`
  color: ${theme.colors.blue};
`

export const statusCompleted = (theme: Theme) => css`
  color: ${theme.colors.green};
`

export const statusFailed = (theme: Theme) => css`
  color: ${theme.colors.red};
`

export const pathText = (theme: Theme) => css`
  display: block;
  max-width: 100%;
  overflow: hidden;
  color: ${theme.typographyColors.typographyThree};
  text-overflow: ellipsis;
  white-space: nowrap;
`

export const iconButton = (theme: Theme) => css`
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 36px;
  height: 36px;
  padding: 8px;
  border-radius: 10px;
  border: 1px solid ${theme.backgrounds.backgroundOne};
  background: ${theme.backgrounds.backgroundFour};
  box-shadow: ${theme.boxShadows.small};

  svg {
    width: 16px;
    height: 16px;
  }
`

export const iconButtonAriaDisabled = css`
  opacity: 0.38;
  cursor: default;

  &:hover {
    background: inherit;
  }
`

export const dialog = css`
  .MuiDialog-paper {
    border-radius: 18px;
  }
`

export const dialogTitle = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: ${theme.spacing(2, 3)};
  font-weight: 700;
`

export const dialogContent = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
  padding: ${theme.spacing(0, 3, 3)};
`

export const modalGrid = (theme: Theme) => css`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: ${theme.spacing(2)};
`

export const modalForm = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`

export const mediaGrid = (theme: Theme) => css`
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: ${theme.spacing(2)};

  @media (max-width: 760px) {
    grid-template-columns: 1fr;
  }
`

export const mediaField = (theme: Theme) => css`
  display: flex;
  min-width: 0;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`

export const filePicker = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(0.75)};
  padding: ${theme.spacing(1.5)};
  border: 1px solid ${theme.backgrounds.backgroundOne};
  border-radius: 12px;
`

export const filePickerHeader = (theme: Theme) => css`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: ${theme.spacing(1)};
`

export const filePickerButton = (theme: Theme) => css`
  &&.Mui-disabled {
    color: ${theme.typographyColors.typographyFour};
  }
`

export const disabledFilePickerButtonWrapper = css`
  display: inline-flex;
  cursor: not-allowed;
`

export const filePickerTitle = css`
  font-weight: 700;
`

export const helperText = (theme: Theme) => css`
  color: ${theme.typographyColors.typographyThree};
`

export const selectedFile = (theme: Theme) => css`
  color: ${theme.colors.green};
`

export const missingAsset = (theme: Theme) => css`
  color: ${theme.typographyColors.typographyFour};
`

export const filePreviewRow = (theme: Theme) => css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
  margin-top: ${theme.spacing(0.5)};
`

export const filePreviewImage = css`
  width: 44px;
  height: 44px;
  object-fit: cover;
  border-radius: 50%;
`

export const inlinePreviewBlock = (theme: Theme) => css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
  margin-top: ${theme.spacing(1)};
`

export const inlinePreviewTitle = css`
  font-weight: 700;
`

export const inlineVideoPreview = (theme: Theme) => css`
  display: block;
  width: min(260px, 100%);
  aspect-ratio: 9 / 16;
  overflow: hidden;
  object-fit: cover;
  border-radius: 12px;
  background: ${theme.colors.black};
`

export const inlineImagePreview = css`
  width: min(260px, 100%);
  aspect-ratio: 9 / 16;
  object-fit: cover;
  border-radius: 12px;
`

export const errorText = (theme: Theme) => css`
  margin-top: -${theme.spacing(1)};
  color: ${theme.colors.red};
`

export const modalActions = (theme: Theme) => css`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing(1.5)};
`
